import { parseRoute } from "./routes";
import { type UiState, render } from "./ui/render";
import { loadOfflineFromBundle, readBundleFromFile } from "./ui/views/offline";
import { loadVerifyView } from "./ui/views/verify";

const STAMP_SVC_BASE =
  (import.meta.env.VITE_STAMP_SVC_BASE as string | undefined) ?? "https://api.sigil.bot";
const ESPLORA_BASE_URLS_RAW =
  (import.meta.env.VITE_ESPLORA_BASE_URLS as string | undefined) ??
  "https://mempool.space/api,https://blockstream.info/api";
const ESPLORA_BASE_URLS = ESPLORA_BASE_URLS_RAW.split(",")
  .map((s) => s.trim())
  .filter(Boolean);

async function main(): Promise<void> {
  const root = document.getElementById("app");
  if (!root) return;

  const route = parseRoute(window.location.pathname);

  if (route.kind === "verify") {
    mount(root, render({ kind: "loading" }));
    const state = await loadVerifyView(route.stampId, {
      stampSvcBase: STAMP_SVC_BASE,
      esploraBaseUrls: ESPLORA_BASE_URLS,
    });
    mount(root, render(state));

    if (
      state.kind === "verify-result" &&
      (state.result.otsState === "pending" || state.result.otsState === "submitted")
    ) {
      setInterval(
        async () => {
          const refreshed = await loadVerifyView(route.stampId, {
            stampSvcBase: STAMP_SVC_BASE,
            esploraBaseUrls: ESPLORA_BASE_URLS,
          });
          mount(root, render(refreshed));
        },
        60 * 60 * 1000,
      );
    }

    return;
  }

  if (route.kind === "offline") {
    mount(root, render({ kind: "offline-empty" }));
    wireOfflineDropzone(root);
    return;
  }

  // notfound
  mount(root, render({ kind: "stamp-not-found" }));
}

function mount(parent: HTMLElement, child: HTMLElement): void {
  while (parent.firstChild) parent.removeChild(parent.firstChild);
  parent.appendChild(child);
}

function wireOfflineDropzone(root: HTMLElement): void {
  const dz = root.querySelector<HTMLDivElement>("#dropzone");
  const fileInput = root.querySelector<HTMLInputElement>("#bundle-file");
  if (!dz || !fileInput) return;

  const consume = async (file: File): Promise<void> => {
    let state: UiState;
    try {
      const bundle = await readBundleFromFile(file);
      state = await loadOfflineFromBundle(bundle, { esploraBaseUrls: ESPLORA_BASE_URLS });
    } catch (e) {
      state = { kind: "bundle-malformed", reason: e instanceof Error ? e.message : String(e) };
    }
    mount(root, render(state));
  };

  dz.addEventListener("click", () => fileInput.click());
  dz.addEventListener("dragover", (e) => e.preventDefault());
  dz.addEventListener("drop", (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0];
    if (file) void consume(file);
  });
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (file) void consume(file);
  });
}

void main();
